// One way to sort a sheet's rows, shared by every grid (Overview, GRP, Service, PDI, Deck…):
// - a header click cycles ascending → descending → back to the sheet's own order;
// - blanks always sink to the bottom, whichever way it runs;
// - numbers, percentages and amounts sort as numbers, ticks as 0 / 1, ISO dates by date, and text in
//   dictionary order (Latin names, then Greek, as the sheets list them; accents and case ignored) with
//   numbers inside it read as numbers ("GT9" before "GT10");
// - the choice survives leaving the page and coming back (per tab, in sessionStorage).
import { useEffect, useState, type KeyboardEvent } from 'react';

export type SortDir = 'asc' | 'desc';
export interface SortState<K> { key: K; dir: SortDir }

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/** a number written the way a sheet writes it ("1,234.5", "1.234,5", "92%", "- 3"), or null. */
function parseNumber(s: string): number | null {
  let t = s.replace(/[\s%]/g, '');
  if (!/^[+-]?[\d.,]*\d[\d.,]*$/.test(t)) return null;
  const lastComma = t.lastIndexOf(','), lastDot = t.lastIndexOf('.');
  if (lastComma >= 0 && lastDot >= 0) {
    // both: whichever comes last is the decimal mark
    t = lastComma > lastDot ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, '');
  } else if (lastComma >= 0) {
    // only commas: "1,234,567" groups thousands, "12,5" is a decimal comma
    t = /^[+-]?\d{1,3}(,\d{3})+$/.test(t) ? t.replace(/,/g, '') : t.replace(',', '.');
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** a cell's sortable form: null when blank, a number for numbers / percentages / ticks, text otherwise
 *  (ISO dates are text that the dictionary order already puts in date order). */
export function sortable(v: unknown): number | string | null {
  if (v == null) return null;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s || s === '–' || s === '-' || s === '—') return null;
  const n = parseNumber(s);
  return n ?? s;
}

/** compare two sortable values in a direction; blanks last either way, numbers before text. */
export function compareSortable(a: number | string | null, b: number | string | null, dir: SortDir): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  let d: number;
  if (typeof a === 'number' && typeof b === 'number') d = a - b;
  else if (typeof a === 'number') d = -1;
  else if (typeof b === 'number') d = 1;
  else d = collator.compare(a, b);
  return dir === 'asc' ? d : -d;
}

/** the rows in sort order (a stable sort on a copy), or the rows untouched when nothing is sorted.
 *  `get` reads the value to sort by for the current key. */
export function sortRows<T, K>(rows: readonly T[], sort: SortState<K> | null, get: (row: T, key: K) => unknown): T[] {
  if (!sort) return rows as T[];
  const keyed = rows.map((row, i) => ({ row, i, v: sortable(get(row, sort.key)) }));
  keyed.sort((x, y) => compareSortable(x.v, y.v, sort.dir) || x.i - y.i);
  return keyed.map((k) => k.row);
}

/** the next state when a header is clicked: a new column starts ascending, then descending, then off. */
export function nextSort<K>(cur: SortState<K> | null, key: K): SortState<K> | null {
  if (!cur || cur.key !== key) return { key, dir: 'asc' };
  return cur.dir === 'asc' ? { key, dir: 'desc' } : null;
}

const STORE = 'rolodex.sort.';
function readStored<K>(id: string | undefined): SortState<K> | null | undefined {
  if (!id) return undefined;
  try {
    const raw = sessionStorage.getItem(STORE + id);
    if (raw == null) return undefined;
    const v = JSON.parse(raw) as SortState<K> | null;
    return v && (v.dir === 'asc' || v.dir === 'desc') ? v : null;
  } catch { return undefined; }
}

/** a table's sort state, with the click cycle, the direction of a column and the props for a sortable
 *  <th>. `id` keeps the choice for this tab (leave it out for a sort that shouldn't stick); `initial` is
 *  the order a first visit starts in (null = the sheet's own order). */
export function useTableSort<K extends string | number>(id?: string, initial: SortState<K> | null = null) {
  const [sort, setSort] = useState<SortState<K> | null>(() => {
    const stored = readStored<K>(id);
    return stored === undefined ? initial : stored;
  });
  useEffect(() => {
    if (!id) return;
    try { sessionStorage.setItem(STORE + id, JSON.stringify(sort)); } catch { /* private mode: not kept */ }
  }, [id, sort]);

  const toggle = (key: K) => setSort((cur) => nextSort(cur, key));
  const dirOf = (key: K): SortDir | null => (sort && sort.key === key ? sort.dir : null);
  /** props for a sortable header cell: clickable, focusable, Enter / Space sorts, aria-sort on the sorted one. */
  const th = (key: K, className?: string) => {
    const d = dirOf(key);
    return {
      className: className ? `sortable ${className}` : 'sortable',
      tabIndex: 0,
      'aria-sort': (d === 'asc' ? 'ascending' : d === 'desc' ? 'descending' : undefined) as 'ascending' | 'descending' | undefined,
      title: d === 'asc' ? 'Sorted A→Z, 0→9. Click for the reverse.' : d === 'desc' ? 'Sorted Z→A, 9→0. Click to go back to the sheet order.' : 'Click to sort',
      onClick: () => toggle(key),
      onKeyDown: (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(key); } },
    };
  };
  return { sort, setSort, toggle, dirOf, th };
}
